import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { BusinessCard } from "@/components/BusinessCard";
import { ShareButton } from "@/components/ShareButton";
import { FavoriteButton } from "@/components/FavoriteButton";
import { StarRating } from "@/components/StarRating";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function Landmark() {
  const { slug } = useParams();
  const { t, lang, tenant, auth } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ rating: 5, body: "", photo_urls: [] });
  const [busy, setBusy] = useState(false);

  const loadReviews = useCallback(async (landmarkId) => {
    const { data: payload } = await api.get("/reviews", {
      params: { reviewable_type: "landmark", reviewable_id: landmarkId },
    });
    setReviews(rows(payload));
  }, []);

  useEffect(() => {
    if (!tenant) return;
    setMissing(false);
    setData(null);
    (async () => {
      try {
        const { data: payload } = await api.get(`/city/landmarks/by-slug/${slug}`, {
          params: { tenant_id: tenant.id },
        });
        setData(payload);
        await loadReviews(payload.landmark.id);
      } catch (e) {
        setMissing(true);
      }
    })();
  }, [slug, tenant, loadReviews]);

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", {
        reviewable_type: "landmark",
        reviewable_id: data.landmark.id,
        rating: form.rating,
        body: form.body,
        photo_urls: form.photo_urls,
      });
      toast.success(t("business.reviewPending"));
      setForm({ rating: 5, body: "", photo_urls: [] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (missing)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8" data-testid="landmark-not-found">
        <p className="text-lg">{t("landmark.notFound")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="landmark-back-btn">
          {t("landmark.backToCity")}
        </Button>
      </div>
    );
  if (!data) return <div className="px-5 py-24 text-center text-muted-foreground">{t("common.loading")}</div>;

  const { landmark, nearby_businesses: nearby } = data;
  const name = localized(lang, landmark, "name");
  const photos = reviews.flatMap((r) => r.photo_urls || []);

  return (
    <div data-testid="landmark-page">
      <div className="relative h-[42vh] min-h-[300px] w-full overflow-hidden bg-muted">
        {landmark.image_url && (
          <img src={resolveImage(landmark.image_url)} alt={name} className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full px-5 pb-8 sm:px-8">
          <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-4">
            <div>
              <Link
                to="/"
                className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="landmark-home-link"
              >
                <ArrowLeft size={14} /> {t("landmark.backToCity")}
              </Link>
              <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="landmark-name">
                {name}
              </h1>
              <p className="mt-2 max-w-2xl text-base text-muted-foreground">{localized(lang, landmark, "summary")}</p>
            </div>
            <div className="flex items-center gap-2">
              <FavoriteButton targetType="landmark" targetId={landmark.id} />
              <ShareButton path={`/place/${landmark.slug}`} title={name} testId="landmark-share-btn" />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap gap-3 border-b border-border py-6" data-testid="landmark-meta">
          {landmark.timings && (
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm">
              <Clock size={14} className="text-[hsl(var(--primary))]" /> {localized(lang, landmark, "timings")}
            </span>
          )}
          {landmark.how_to_reach && (
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm">
              <Navigation size={14} className="text-[hsl(var(--primary))]" /> {localized(lang, landmark, "how_to_reach")}
            </span>
          )}
          {landmark.address && (
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm">
              <MapPin size={14} className="text-[hsl(var(--primary))]" /> {landmark.address}
            </span>
          )}
        </div>

        <div className="grid gap-10 py-12 lg:grid-cols-[1.4fr_0.6fr]">
          <div className="space-y-10">
            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">{t("landmark.about")}</h2>
              <p className="whitespace-pre-line text-base leading-relaxed text-muted-foreground">
                {localized(lang, landmark, "description")}
              </p>
            </section>

            {photos.length > 0 && (
              <section className="space-y-3" data-testid="landmark-photos">
                <h2 className="font-heading text-xl font-semibold tracking-tight">{t("landmark.photos")}</h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {photos.slice(0, 9).map((url) => (
                    <img key={url} src={resolveImage(url)} alt={name} width={400} height={128} loading="lazy" decoding="async" className="h-32 w-full rounded-xl object-cover" />
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-4" data-testid="landmark-reviews">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">{t("landmark.reviews")}</h2>
              {reviews.length === 0 && <p className="text-sm text-muted-foreground">{t("landmark.reviewsEmpty")}</p>}
              <div className="space-y-3">
                {reviews.map((r) => (
                  <article key={r.id} className="rounded-2xl border border-border p-5" data-testid={`landmark-review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{r.author_name}</p>
                      <StarRating value={r.rating} />
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
                  </article>
                ))}
              </div>

              {auth && (
                <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="landmark-review-form">
                  <h3 className="text-sm font-semibold">{t("landmark.addReview")}</h3>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        data-testid={`landmark-rating-${n}`}
                        onClick={() => setForm({ ...form, rating: n })}
                        className={`h-9 w-9 rounded-full border text-sm ${
                          form.rating === n
                            ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                            : "border-border"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <Textarea
                    rows={3}
                    data-testid="landmark-review-input"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                  />
                  <ImageUpload
                    label={t("landmark.addPhotos")}
                    value=""
                    onChange={(url) => setForm({ ...form, photo_urls: [...form.photo_urls, url] })}
                    testId="landmark-review-photo"
                  />
                  {form.photo_urls.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="landmark-review-photo-count">
                      {form.photo_urls.length}
                    </p>
                  )}
                  <Button
                    className="rounded-full"
                    disabled={busy}
                    data-testid="landmark-review-submit"
                    onClick={submitReview}
                  >
                    {busy ? t("common.saving") : t("business.writeReview")}
                  </Button>
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-4" data-testid="landmark-nearby">
            <h2 className="font-heading text-xl font-semibold tracking-tight">{t("landmark.nearby")}</h2>
            <div className="space-y-4">
              {nearby.map((b) => (
                <BusinessCard key={b.id} business={b} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
